/**
 * DM Harness Control Plane W0 PR-3 (gh#2614) — the `pm_project.settings` CONTRACT.
 *
 * One schema, three consumers: the PATCH door validates against it, the web form is
 * WALKED out of it, and the CLI's refusal quotes its leaf list. A second hand-written
 * field roster anywhere would be a second authority that agrees only on the day it is
 * written.
 *
 * ── NO `.default()` ANYWHERE, AND THAT IS A CORRECTNESS RULE ────────────────
 * This schema is parsed at a PATCH boundary. A `.default()` materialises a value for
 * every key the caller did NOT send, so the merge would then write those defaults over
 * whatever is stored — a request that set one field would silently reset its siblings
 * ([[feedback_schema_default_at_patch_boundary_overwrites_stored]]). Every node is
 * `.optional()` instead, so "absent" survives the parse as absent.
 *
 * ── `null` IS UNSET, WHICH IS WHY EVERY NODE IS ALSO `.nullable()` ──────────
 * `undefined` (absent) and `null` (sent) must mean different things at a PATCH: leave it
 * alone vs remove it. Without the nullable arm the second is unexpressible and a caller
 * has no way to clear a key they set by mistake.
 *
 * ── EVERY OBJECT IS `.strict()` ────────────────────────────────────────────
 * `pm_project.settings` is a SHARED bag: the sprint mirror's sweep marker lives in it
 * (`sprint_mirror`) and further server-owned namespaces will. A permissive parse would
 * let a caller write a namespace nothing reads — or shadow one the server owns — and the
 * mistake would only surface as a sweep that stopped working. Unknown keys are refused
 * BY PATH, and the refusal enumerates {@link PM_PROJECT_SETTINGS_LEAF_KEYS}: that is a
 * compiled-in constant, not tenant data, so enumerating it is not the disclosure
 * [[feedback_ambiguity_refusal_enumeration_oracle]] warns about.
 */
import { z } from 'zod';
import { DM_TREE_HIDE_CLASSES } from './pmTreeView.js';
import { dmShipPathSchema } from './dmJob.js';
/**
 * Top-level namespaces the SERVER writes and a caller may not. Refused with their own
 * sentence rather than as "unknown key" — they are known, and the repair is different.
 */
export const PM_PROJECT_SETTINGS_SERVER_OWNED_KEYS = ['sprint_mirror'];
/**
 * `memory.default_scope`'s vocabulary (the pre-D16 scopes; no consumer reads it). The api-side `DM_MEMORY_SCOPE_VALUES`, spelled
 * here because `@elmes/shared` cannot depend on `@elmes/api` — `pmProductKind.ts`'s
 * posture for the same four words.
 *
 * The EQUALITY is pinned from the side that owns it:
 * `packages/api/src/modules/pm/__tests__/pmProjectSettingsVocabulary.test.ts`. No test in
 * this package can compare them, so without that file the spelling would be a second
 * authority nothing measures.
 */
export const PM_PROJECT_SETTINGS_MEMORY_SCOPES = [
    'tenant_global',
    'tech',
    'project',
    'module',
];
/** W4-H2 S5 — the policy guard's autonomy modes (`policy.mode`); the guard's jq spells them too. */
export const PM_PROJECT_SETTINGS_POLICY_MODES = ['strict', 'default', 'yolo'];
export const PM_PROJECT_SETTINGS_POLICY_MODE_DEFAULT = 'default';
/** W4-M2b — `memory.projection.mode`: off = no hook output, check = report only, apply = write (human door only). */
export const PM_PROJECT_SETTINGS_MEMORY_PROJECTION_MODES = ['off', 'check', 'apply'];
export const PM_PROJECT_SETTINGS_MEMORY_PROJECTION_MODE_DEFAULT = 'check';
/** W5-7 (K12) — `dm.containment.mode`: warn = record a refused placement, deny = refuse it (422). */
export const PM_PROJECT_SETTINGS_DM_CONTAINMENT_MODES = ['warn', 'deny'];
export const PM_PROJECT_SETTINGS_DM_CONTAINMENT_MODE_DEFAULT = 'warn';
/** T1-K1 — most root areas `dm.topic_gate.area_ids` may gate. */
export const PM_PROJECT_SETTINGS_DM_TOPIC_GATE_AREA_IDS_MAX = 32;
/** `pm_item.id` is int4: a larger id RAISES 22003 in the human door's lookup (a 500, not "not a root"). */
const PM_ITEM_ID_MAX = 2_147_483_647;
/** `policy.attended_window_min` when unset, and its ceiling (one day). */
export const PM_PROJECT_SETTINGS_ATTENDED_WINDOW_DEFAULT_MIN = 30;
export const PM_PROJECT_SETTINGS_ATTENDED_WINDOW_MAX_MIN = 1440;
/** `memory.promotion.require_second_approver` when unset: one actor applies a promotion at once (W4-M1b). */
export const PM_PROJECT_SETTINGS_MEMORY_REQUIRE_SECOND_APPROVER_DEFAULT = false;
/** D21 stale sweep: a record un-cited and un-validated this many days is filed for review (W4-M3). */
export const PM_PROJECT_SETTINGS_MEMORY_STALE_AFTER_DAYS_DEFAULT = 90;
export const PM_PROJECT_SETTINGS_MEMORY_STALE_AFTER_DAYS_MAX = 3650;
/** D21 stale sweep: the open-proposal ceiling per project; a tick files up to it, oldest basis first. */
export const PM_PROJECT_SETTINGS_MEMORY_STALE_SWEEP_BATCH_DEFAULT = 25;
export const PM_PROJECT_SETTINGS_MEMORY_STALE_SWEEP_BATCH_MAX = 200;
/** W6-2 (K1) — a pending CI run unheard-of this long is re-read from GitHub REST; ceiling one day. */
export const PM_PROJECT_SETTINGS_GITHUB_CI_STALE_AFTER_SEC_DEFAULT = 180;
export const PM_PROJECT_SETTINGS_GITHUB_CI_STALE_AFTER_SEC_MAX = 86_400;
/** W6-4 (K3) — the ship job's step budgets and its loopback health route (`app.ts` mounts `/api/health`). */
export const PM_PROJECT_SETTINGS_SHIP_CI_TIMEOUT_SEC_DEFAULT = 2400;
export const PM_PROJECT_SETTINGS_SHIP_DEPLOY_TIMEOUT_SEC_DEFAULT = 1320;
export const PM_PROJECT_SETTINGS_SHIP_TIMEOUT_SEC_MAX = 14_400;
export const PM_PROJECT_SETTINGS_SHIP_HEALTH_PATH_DEFAULT = '/api/health';
const text = z.string().min(1);
const positiveInt = z.number().int().positive();
const budgetInt = z.number().int().nonnegative();
/** `.optional()` + `.nullable()` — see the header for why both, at every node. */
function node(inner) {
    return inner.nullable().optional();
}
export const pmProjectSettingsSchema = z
    .object({
    /** Host-specific absolute paths. The server STORES them; only `dm project verify` can check them. */
    paths: node(z
        .object({
        project_path: node(text),
        worktrees_root: node(text),
        sprint_dir: node(text),
        runbook_dir: node(text),
        memory_dir: node(text),
    })
        .strict()),
    git: node(z
        .object({
        default_branch: node(text),
        release_branch: node(text),
        branch_prefixes: node(z.array(text)),
    })
        .strict()),
    github: node(z
        .object({
        /** `owner/name`, display only. NOT a second authority over `pm_project.repo_ref` — that column is the parsed identity; this is the harness' spelling of it and W1 reconciles them (gh#2614). Nor a CI ledger binding: that is the operator's `DM_CI_REPO_BINDINGS` (W6-2). */
        repo: node(text),
        ci_workflow: node(text),
        ci_stale_after_sec: node(positiveInt.max(PM_PROJECT_SETTINGS_GITHUB_CI_STALE_AFTER_SEC_MAX)),
    })
        .strict()),
    inbox: node(z
        .object({
        cli_live_window_sec: node(positiveInt),
    })
        .strict()),
    memory: node(z
        .object({
        default_scope: node(z.enum(PM_PROJECT_SETTINGS_MEMORY_SCOPES)),
        promotion: node(z.object({ require_second_approver: node(z.boolean()) }).strict()),
        inject: node(z.object({ top_k: node(positiveInt), token_budget: node(positiveInt) }).strict()),
        projection: node(z.object({ mode: node(z.enum(PM_PROJECT_SETTINGS_MEMORY_PROJECTION_MODES)) }).strict()),
        stale_after_days: node(positiveInt.max(PM_PROJECT_SETTINGS_MEMORY_STALE_AFTER_DAYS_MAX)),
        stale_sweep: node(z
            .object({ batch: node(positiveInt.max(PM_PROJECT_SETTINGS_MEMORY_STALE_SWEEP_BATCH_MAX)) })
            .strict()),
    })
        .strict()),
    policy: node(z
        .object({
        locked_keys: node(z.array(text)),
        /** W4-H2 S5 autonomy mode; absent = `default`. `yolo` is written only through the human door. */
        mode: node(z.enum(PM_PROJECT_SETTINGS_POLICY_MODES)),
        /** Minutes since the last human prompt a session still counts as attended; absent = 30. */
        attended_window_min: node(positiveInt.max(PM_PROJECT_SETTINGS_ATTENDED_WINDOW_MAX_MIN)),
    })
        .strict()),
    /** Per-class SessionStart/prompt injection ceilings, in tokens (W0 İ1). Consumed by PR-4. */
    inject: node(z
        .object({
        budget: node(z
            .object({
            bootstrap: node(budgetInt),
            policy: node(budgetInt),
            memory: node(budgetInt),
            delta: node(budgetInt),
        })
            .strict()),
    })
        .strict()),
    /**
     * `pm_item.data`'s BYTE CEILINGS, per content class (dm.spec 637 madde 2, W2b C2).
     *
     * The four keys are SPELLED here and pinned against `DM_CONTENT_CAP_KEYS` by
     * `__tests__/pmProjectSettings.test.ts` — the `pmProductKind` posture for a
     * vocabulary this schema cannot import without a cycle (`dmContentCap.ts` reads
     * {@link readPmProjectSettingsPath} from THIS module, so the dependency runs one
     * way only). Deriving the shape from that tuple would have cost the leaf types,
     * which is what the web form renders from.
     *
     * `positiveInt`: a cap of zero is not "no cap", it is a door that refuses every
     * document, and a project that means "unbounded" removes the key (`null`) rather
     * than spelling a number that reads as a bound.
     */
    content: node(z
        .object({
        cap: node(z
            .object({
            item: node(positiveInt),
            epic: node(positiveInt),
            plan: node(positiveInt),
            spec: node(positiveInt),
        })
            .strict()),
    })
        .strict()),
    /**
     * The Tree-Table's PROJECT DEFAULTS (W4, worklist 3.7) — a starting view, never a
     * ceiling: both are overridden by the reader's own `pm_user_view_pref` row.
     */
    tree: node(z
        .object({
        /**
         * The `item_no` the tree opens anchored on. An `item_no` and not a `pm_item.id`,
         * because `?rootId=` — the address this seeds — speaks that alphabet
         * ([[feedback_id_namespaces]]). Taxonomy (`area`) is a later wave's meaning.
         */
        default_root: node(positiveInt),
        /** Hide classes a fresh reader starts with. Closed vocabulary: a typo is a 422. */
        hidden_default: node(z.array(z.enum(DM_TREE_HIDE_CLASSES))),
    })
        .strict()),
    /** Session handoff (DM 2123): a sprint-less `[checkpoint]` lands on this item's thread and every SessionStart bootstrap shows its newest one. */
    checkpoint: node(z
        .object({
        /** A `pm_item.id` — NOT an `item_no` (contrast `tree.default_root`). */
        anchor_item_id: node(positiveInt),
    })
        .strict()),
    /** The `dm.job` lane runtime's project-level knobs (DM 2126 S1a). */
    lane: node(z
        .object({
        /**
         * Lanes that must not be dispatched to — the STOP SWITCH (K4).
         *
         * A list of lane names rather than `lanes[<lane>].paused`: a record would need
         * its own walker case, its own path-pattern check and its own form control for
         * a fact this shape already carries. Membership IS paused.
         */
        paused_lanes: node(z.array(text)),
        /**
         * `pm_item.id` of the project's queue container — the dm.item jobs are filed
         * under. A pointer, so the container can be renamed or re-parented without
         * the queue losing it.
         */
        queue_item_id: node(positiveInt),
    })
        .strict()),
    /** W6-4 (K3) — the ship job; absent = the defaults above. */
    ship: node(z
        .object({
        ci_timeout_sec: node(positiveInt.max(PM_PROJECT_SETTINGS_SHIP_TIMEOUT_SEC_MAX)),
        deploy_timeout_sec: node(positiveInt.max(PM_PROJECT_SETTINGS_SHIP_TIMEOUT_SEC_MAX)),
        health_path: node(dmShipPathSchema),
    })
        .strict()),
    /** W5-7 (K12) — the containment matrix's enforcement; absent = warn. Leaving deny is the human door's. */
    dm: node(z
        .object({
        containment: node(z.object({ mode: node(z.enum(PM_PROJECT_SETTINGS_DM_CONTAINMENT_MODES)) }).strict()),
        /**
         * T1-K1 (#3961) — ROOT dm.area ids (`pm_item.id`) whose waves/standalones must name a topic
         * (`requires_topic_edge`); absent/empty = the gate is passive. Any change is the human door's.
         */
        topic_gate: node(z
            .object({
            area_ids: node(z.array(positiveInt.max(PM_ITEM_ID_MAX)).max(PM_PROJECT_SETTINGS_DM_TOPIC_GATE_AREA_IDS_MAX)),
        })
            .strict()),
    })
        .strict()),
})
    .strict();
function defOf(schema) {
    return schema._def;
}
/**
 * Peel `.optional()` / `.nullable()` wrappers.
 *
 * Discriminated on `_def.typeName` rather than `instanceof`: shared, api and web can
 * each resolve their own zod copy under pnpm, and an `instanceof` across two copies is
 * false for a value that is plainly the right type.
 */
function unwrap(schema) {
    let current = schema;
    for (;;) {
        const def = defOf(current);
        if ((def.typeName === 'ZodOptional' || def.typeName === 'ZodNullable') && def.innerType) {
            current = def.innerType;
            continue;
        }
        return current;
    }
}
/**
 * Every settable LEAF, in declaration order.
 *
 * THROWS on a node shape it cannot describe. That is the point: a namespace added with a
 * type the form cannot render would otherwise be silently absent from the UI while the
 * API accepted it, which is the drift this walker exists to prevent.
 */
export function walkPmProjectSettingsLeaves(schema, prefix = []) {
    const inner = unwrap(schema);
    const def = defOf(inner);
    const leaf = (kind, options) => [
        { path: prefix, key: prefix.join('.'), kind, ...(options === undefined ? {} : { options }) },
    ];
    switch (def.typeName) {
        case 'ZodObject': {
            const shape = inner.shape;
            return Object.keys(shape).flatMap((key) => walkPmProjectSettingsLeaves(shape[key], [...prefix, key]));
        }
        case 'ZodString':
            return leaf('string');
        case 'ZodNumber':
            return leaf('integer');
        case 'ZodBoolean':
            return leaf('boolean');
        case 'ZodEnum':
            return leaf('enum', inner.options);
        case 'ZodArray': {
            const element = unwrap(inner.element);
            const elementType = defOf(element).typeName;
            // A CLOSED list keeps its options, so the form can refuse a typo locally and the 422
            // is the second answer rather than the only one.
            if (elementType === 'ZodEnum') {
                return leaf('enum-list', element.options);
            }
            if (elementType === 'ZodNumber') {
                // The form parses whole numbers only: a list without `.int()` would be drawn with the wrong parser.
                if (!element.isInt) {
                    throw new Error(`pmProjectSettings: '${prefix.join('.')}' is an array of non-integer ZodNumber, which no form ` +
                        'control renders. Add `.int()` or give the walker a case for it in the same edit that adds the key.');
                }
                return leaf('integer-list');
            }
            if (elementType !== 'ZodString') {
                throw new Error(`pmProjectSettings: '${prefix.join('.')}' is an array of ` +
                    `${String(elementType)}, which no form control renders. Give the ` +
                    'walker a case for it in the same edit that adds the key.');
            }
            return leaf('string-list');
        }
        default:
            throw new Error(`pmProjectSettings: '${prefix.join('.')}' has node type ${String(def.typeName)}, which ` +
                'the leaf walker does not describe. Add a case rather than leaving the key out of the form.');
    }
}
/** The walked leaves, computed once. The form renders these and nothing else. */
export const PM_PROJECT_SETTINGS_LEAVES = walkPmProjectSettingsLeaves(pmProjectSettingsSchema);
/** Dotted leaf keys — the `allowed` list a 422 publishes. */
export const PM_PROJECT_SETTINGS_LEAF_KEYS = PM_PROJECT_SETTINGS_LEAVES.map((entry) => entry.key);
/** Top-level namespaces a caller may write. `sprint_mirror` is deliberately absent. */
export const PM_PROJECT_SETTINGS_NAMESPACES = Object.keys(pmProjectSettingsSchema.shape);
/** Read a dotted path out of a stored settings document; `undefined` when any hop is absent. */
export function readPmProjectSettingsPath(document, path) {
    let current = document;
    for (const segment of path) {
        if (current === null || typeof current !== 'object' || Array.isArray(current))
            return undefined;
        current = current[segment];
    }
    return current;
}
/** `memory.projection.mode` of a stored settings document; unset, null or unknown = check. */
export function pmProjectSettingsMemoryProjectionModeOf(settings) {
    const value = readPmProjectSettingsPath(settings, ['memory', 'projection', 'mode']);
    return PM_PROJECT_SETTINGS_MEMORY_PROJECTION_MODES.includes(value)
        ? value
        : PM_PROJECT_SETTINGS_MEMORY_PROJECTION_MODE_DEFAULT;
}
/** `github.ci_stale_after_sec` of a stored settings document; unset, null or out of range = 180. */
export function pmProjectSettingsGithubCiStaleAfterSecOf(settings) {
    const value = readPmProjectSettingsPath(settings, ['github', 'ci_stale_after_sec']);
    return typeof value === 'number' &&
        Number.isInteger(value) &&
        value > 0 &&
        value <= PM_PROJECT_SETTINGS_GITHUB_CI_STALE_AFTER_SEC_MAX
        ? value
        : PM_PROJECT_SETTINGS_GITHUB_CI_STALE_AFTER_SEC_DEFAULT;
}
/** `dm.containment.mode` of a stored settings document; unset, null or unknown = warn. */
export function pmProjectSettingsDmContainmentModeOf(settings) {
    const value = readPmProjectSettingsPath(settings, ['dm', 'containment', 'mode']);
    return PM_PROJECT_SETTINGS_DM_CONTAINMENT_MODES.includes(value)
        ? value
        : PM_PROJECT_SETTINGS_DM_CONTAINMENT_MODE_DEFAULT;
}
/** `dm.topic_gate.area_ids` of a stored settings document: its positive integers, de-duplicated; else `[]`. */
export function pmProjectSettingsDmTopicGateAreaIdsOf(settings) {
    const value = readPmProjectSettingsPath(settings, ['dm', 'topic_gate', 'area_ids']);
    if (!Array.isArray(value))
        return [];
    return [...new Set(value.filter((id) => typeof id === 'number' && Number.isInteger(id) && id > 0))];
}
/** `ship.*` of a stored settings document; an unset or unreadable leaf takes its default. */
export function pmProjectSettingsShipOf(settings) {
    const timeout = (key, fallback) => {
        const value = readPmProjectSettingsPath(settings, ['ship', key]);
        return typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= PM_PROJECT_SETTINGS_SHIP_TIMEOUT_SEC_MAX
            ? value
            : fallback;
    };
    const healthPath = readPmProjectSettingsPath(settings, ['ship', 'health_path']);
    return {
        ciTimeoutSec: timeout('ci_timeout_sec', PM_PROJECT_SETTINGS_SHIP_CI_TIMEOUT_SEC_DEFAULT),
        deployTimeoutSec: timeout('deploy_timeout_sec', PM_PROJECT_SETTINGS_SHIP_DEPLOY_TIMEOUT_SEC_DEFAULT),
        healthPath: dmShipPathSchema.safeParse(healthPath).success
            ? healthPath
            : PM_PROJECT_SETTINGS_SHIP_HEALTH_PATH_DEFAULT,
    };
}
//# sourceMappingURL=pmProjectSettings.js.map